import api from "../api/api";

// Thin wrapper around the real, existing, public product endpoints —
// GET /products, GET /products/{id} (both confirmed live, no auth
// required). No endpoint is invented; no client-side filtering
// substitutes for a server-side param the backend doesn't have (there is
// no "sort" param, for example — that's a genuine backend limitation,
// not something to fake here).
export async function getProducts({ search, categoryId, page = 1, limit = 24 } = {}) {
  const res = await api.get("/products", {
    params: {
      active_only: true,
      search: search || undefined,
      category_id: categoryId || undefined,
      page,
      limit,
    },
  });
  // Only ?page=1.. returns the {items,total,...} envelope; without it the
  // backend returns a plain array. Always requesting a page here keeps
  // this one call site consistent for every caller (Shop, Categories).
  return res.data; // { items, total, page, limit, total_pages }
}

export async function getProduct(productId) {
  const res = await api.get(`/products/${productId}`);
  return res.data; // ProductOut
}

// GET /products/{id}/units — the real, existing multi-unit/pricing
// endpoint (confirmed live, no auth required). Returns ProductUnitOut
// rows; ⚠️ each row includes `cost_price` — never render it, same rule
// as ProductOut's own cost_price.
export async function getProductUnits(productId) {
  const res = await api.get(`/products/${productId}/units`);
  return res.data; // ProductUnitOut[] — may be an empty array
}

// Phase 40 — Smart Voice Order Input: the full active catalog for local,
// in-browser voice/product-name matching. Same GET /products endpoint
// Shop.jsx already uses, just requesting every active product in one call
// (omitting `page` returns the bare array, not the paginated envelope) so
// voice matching runs against an in-memory list instead of re-querying the
// backend per spoken word. `limit=200` is the endpoint's own maximum; if a
// shop's active catalog ever exceeds that, voice matching would only see
// the first page — a real scaling limit, not something to silently paper
// over with client-side pagination here.
export async function getAllProductsForVoice() {
  const res = await api.get("/products", { params: { active_only: true, limit: 200 } });
  return res.data; // ProductOut[]
}

// Phase 42 — Global Voice Training: the one public, unauthenticated
// consumption feed (GET /voice-training/active — backend/routers/
// voice_training.py). Returns only {intent, product_id, unit_id,
// spoken_phrase, language} for training rows that are active AND still
// reference a real, currently-active product/unit — never the full
// management payload (no id, no created_by, no timestamps). Management
// (create/update/delete) stays entirely inside frontend_Admin's own
// Setup > Voice Training page; nothing here can modify training data.
export async function getActiveVoiceTraining() {
  const res = await api.get("/voice-training/active");
  return res.data; // { intent, product_id, unit_id, spoken_phrase, language }[]
}

// GET /products/units/batch — units for many products in ONE request (the
// shop grid used to call /products/{id}/units once per card). Returns
// { [productId]: ProductUnitPublicOut[] }; no cost fields.
export async function getProductUnitsBatch(productIds) {
  if (!productIds.length) return {};
  const res = await api.get("/products/units/batch", { params: { ids: productIds.join(",") } });
  return res.data;
}
